import { all, call, fork, put, takeEvery, select } from "redux-saga/effects";
import { types } from "./types";
import { types as companyTypes } from "../company/types";
import { FETCH_GROUPS } from "../../api/group";

const getCompanyId = (state) => state.authReducer.userDetails.companyId;
const getIsAdmin = (state) => state.authReducer.userDetails.isAdmin;
const getGroupId = (state) => state.authReducer.userDetails.groupId;
const getUserId = (state) => state.authReducer.userDetails._id;
const getWorkspaces = (state) => state.workspacesReducer.workspaces;
const getSelectedWorkspace = (state) => state.workspacesReducer.selectedWorkspace;
const getSelectedWorkspaceGroupId = (state) => state.workspacesReducer.selectedWorkspaceGroupId;

const matchWorkspaceId = (workspace, id) =>
  id != null && workspace?._id != null && String(workspace._id) === String(id);

// Remember the active workspace per user so a page refresh doesn't dump
// the viewer back into their default (first) workspace. Tagged with the
// user's own groupId at the time it was written -- an admin deliberately
// switching which workspace they're browsing is a choice worth
// remembering, but if their *own* groupId is reassigned elsewhere (e.g. in
// Player Management) since then, that old choice is stale and must not
// keep overriding their now-current workspace.
const workspaceStorageKey = (userId) => `gmb.activeWorkspace.${userId || "anon"}`;

const readStoredWorkspace = (userId) => {
  try {
    const raw = localStorage.getItem(workspaceStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
};

const storeWorkspace = (userId, workspaceId, groupId) => {
  try {
    if (workspaceId) {
      localStorage.setItem(
        workspaceStorageKey(userId),
        JSON.stringify({ workspaceId, groupId: groupId ?? null }),
      );
    }
  } catch (_) {
    // storage unavailable (private mode) — selection just won't persist
  }
};

function* GetWorkspaces(action) {
  try {
    const companyId = yield select(getCompanyId);
    const { data: workspaces } = yield call(() => FETCH_GROUPS(companyId));

    yield put({
      type: types.GET_ALL_WORKSPACES_SUCCESS,
      workspaces,
    });

    const { selectedWorkspace: explicitSelection } = action;
    let currentWorkspace = explicitSelection;
    const groupId = yield select(getGroupId);

    if (!currentWorkspace) {
      // redux-persist rehydrates this whole slice from localStorage before
      // this saga ever runs, so `existing` may just be last session's
      // choice -- not necessarily made under the groupId we resolved above.
      // Only trust it if it was.
      const existing = yield select(getSelectedWorkspace);
      const existingGroupId = yield select(getSelectedWorkspaceGroupId);
      if (existing?._id && String(existingGroupId ?? "") === String(groupId ?? "")) {
        currentWorkspace = workspaces.find((w) => matchWorkspaceId(w, existing._id));
      }
    }

    if (!currentWorkspace) {
      const userId = yield select(getUserId);
      const stored = readStoredWorkspace(userId);
      // Only trust the cached choice if it was written under the user's
      // *current* groupId -- otherwise their own workspace assignment
      // changed since, and the cache is stale (it would otherwise keep
      // pointing at the workspace they were reassigned out of).
      currentWorkspace =
        stored && String(stored.groupId ?? "") === String(groupId ?? "")
          ? workspaces.find((w) => matchWorkspaceId(w, stored.workspaceId))
          : null;
    }

    if (!currentWorkspace) {
      currentWorkspace = workspaces.find((w) => matchWorkspaceId(w, groupId));
    }

    yield put({
      type: types.SET_SELECTED_WORKSPACE,
      selectedWorkspace: currentWorkspace || workspaces[0],
    });
  } catch (err) {
    yield put({ type: types.GET_ALL_WORKSPACES_FAILED, error: err.message });
  }
}

function* SetSelectedWorkspace(action) {
  try {
    let { selectedWorkspace } = action;
    
    if(!selectedWorkspace) {
      const isAdmin = yield select(getIsAdmin);
      const groupId = yield select(getGroupId);
      const workspaces = yield select(getWorkspaces);
      
      if(!isAdmin && groupId) {
        selectedWorkspace = workspaces.find((row) => matchWorkspaceId(row, groupId));
      } else {
        // eslint-disable-next-line prefer-destructuring
        selectedWorkspace  = workspaces[0];
      }
    }
    const userId = yield select(getUserId);
    const groupId = yield select(getGroupId);
    storeWorkspace(userId, selectedWorkspace?._id, groupId);
    yield put({
      type: types.SET_SELECTED_WORKSPACE_SUCCESS,
      selectedWorkspace,
      groupId,
    });
  } catch (err) {
    yield put({ type: types.SET_SELECTED_WORKSPACE_FAILED, error: err.message });
  }
}

function* watchGetJobsRequest() {
  yield takeEvery(types.GET_ALL_WORKSPACES, GetWorkspaces);
  yield takeEvery(companyTypes.GET_COMPANY_SUCCESS, GetWorkspaces);
  yield takeEvery(types.SET_SELECTED_WORKSPACE, SetSelectedWorkspace);
}

// root saga container
function* rootSaga() {
  yield all([fork(watchGetJobsRequest)]);
}

export default rootSaga;
